/**
 * The cycle's price space on one line: where its ladder rests, what it paid on
 * average, where it leaves, and where the price is now.
 *
 * One glance answers what a row of figures made the reader work out: how far
 * the price is from the average and from the target, and how much of the
 * ladder is bought. Positions are geometry only; every figure arrived exact.
 */
import type { SlotResponse } from "@/shared/api";
import { slotQuote } from "@/shared/cycles/quote";
import { finiteGeometryValue } from "@/shared/money/geometry";

function price(text: string | null | undefined): number | null {
  if (text == null) return null;
  const value = finiteGeometryValue(text);
  return value !== null && value > 0 ? value : null;
}

export type RailPoints = {
  readonly rungs: readonly { readonly price: number; readonly bought: boolean }[];
  readonly average: number | null;
  readonly target: number | null;
  readonly current: number | null;
};

export function railPoints(slot: SlotResponse): RailPoints {
  const levels = slot.evidence?.placement?.levels ?? [];
  return {
    rungs: levels
      .filter((level) => level.state !== "CANCELLED")
      .flatMap((level) => {
        const at = price(level.weighted_fill_price ?? level.planned_order_price);
        return at === null
          ? []
          : [{ price: at, bought: level.state === "FILLED" || level.state === "PARTIALLY_FILLED" }];
      }),
    average: price(slot.vwap),
    target: price(slot.outlook?.target_price ?? slot.exit_arm_price),
    current: price(slot.current_price),
  };
}

/** A position in per cent for each price, lowest left, over every mark. */
export function railScale(points: RailPoints): ((value: number) => number) | null {
  const all = [
    ...points.rungs.map((rung) => rung.price),
    points.average,
    points.target,
    points.current,
  ].filter((value): value is number => value !== null);
  if (all.length < 2) return null;
  const low = Math.min(...all);
  const high = Math.max(...all);
  if (high <= low) return null;
  return (value: number) => 3 + ((value - low) / (high - low)) * 94;
}

const at = (left: number): React.CSSProperties => ({ left: `${left.toFixed(2)}%` });

export function CycleRail({
  slot,
  testId,
}: {
  readonly slot: SlotResponse;
  readonly testId?: string;
}): React.JSX.Element | null {
  const points = railPoints(slot);
  const x = railScale(points);
  if (x === null) return null;
  const current = points.current === null ? null : x(points.current);
  const target = points.target === null ? null : x(points.target);
  const underwater =
    points.current !== null && points.average !== null && points.current < points.average;
  const q = slotQuote(slot);
  const said = [
    slot.current_price == null ? null : `сейчас ${q(slot.current_price)}`,
    slot.vwap == null ? null : `средняя ${q(slot.vwap)}`,
    points.target === null ? null : `цель ${q(slot.outlook?.target_price ?? slot.exit_arm_price ?? "")}`,
  ]
    .filter((part) => part !== null)
    .join(", ");
  return (
    <div
      className="relative h-4 w-full"
      role="img"
      aria-label={`Линейка цикла: ${said}`}
      title={said}
      data-testid={testId}
    >
      <span aria-hidden="true" className="absolute top-1/2 h-px w-full -translate-y-1/2 bg-border-strong" />
      {/* What is left to go: from the price now to the target. */}
      {current !== null && target !== null && (
        <span
          aria-hidden="true"
          className="absolute top-1/2 h-1 -translate-y-1/2 rounded-full bg-chart-positive/40"
          style={{ left: `${Math.min(current, target).toFixed(2)}%`, width: `${Math.abs(target - current).toFixed(2)}%` }}
        />
      )}
      {points.rungs.map((rung, index) => (
        <span
          key={index}
          aria-hidden="true"
          className={`absolute top-1/2 w-px -translate-x-1/2 -translate-y-1/2 ${
            rung.bought ? "h-3 bg-text-secondary" : "h-2 bg-text-muted/60"
          }`}
          style={at(x(rung.price))}
        />
      ))}
      {points.average !== null && (
        <span
          aria-hidden="true"
          className="absolute top-0 h-4 w-0.5 -translate-x-1/2 bg-text-primary"
          style={at(x(points.average))}
        />
      )}
      {target !== null && (
        <span
          aria-hidden="true"
          className="absolute top-1/2 size-2 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-chart-positive"
          style={at(target)}
        />
      )}
      {current !== null && (
        <span
          aria-hidden="true"
          className={`absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface-default ${
            underwater ? "bg-chart-negative" : "bg-chart-positive"
          }`}
          style={at(current)}
        />
      )}
    </div>
  );
}
